import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  EndpointConfig,
  UseApiRequest,
  RequestConfig
} from "src/core/shared/api/config.api";
import { IOrder } from "src/core/orders/api/orders.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { ICustomer } from "src/core/customers/api/customers.api";

export type InvoiceStatus =
  | "draft"
  | "sent"
  | "outstanding"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "disputed"
  | "written_off"
  | "cancelled";

export type InvoiceType = string; // Backend will define specific types

export type TransactionType =
  | "charge"
  | "refund"
  | "invoice"
  | "payment"
  | "credit_note"
  | "debit_note";

export type TransactionStatus =
  | "requires_payment_method"
  | "requires_confirmation"
  | "requires_action"
  | "requires_capture"
  | "processing"
  | "canceled"
  | "succeeded"
  | "failed"
  | "paid"
  | "overdue";

export type TransactionProvider = "unity" | "stripe" | string;

export type TransactionSessionType = "on_session" | "off_session";

export type TransactionNextAction = string; // Backend will define specific types

export type TransactionError = string; // Backend will define specific types

export interface Invoice {
  id: number;
  externalId: string;
  invoiceNumber: string;
  branchId?: number;
  branch?: IDrop; // Branch with customer info for admin views
  customerId?: number
  customer?: ICustomer
  customerReference?: string;
  invoiceDate: string; // ISO date string
  dueDate?: string; // ISO date string
  totalAmount: number;
  vatAmount: number; // VAT amount
  outstandingAmount: number;
  currencyCode: string;
  status: InvoiceStatus;
  type: InvoiceType;
  pdfKey?: string; // URL to PDF invoice
  notes?: string;
  transactions?: Transaction[]; // For "Transactions" tab on Invoice Details
  orderCount?: number; // Count of related orders (orders fetched via separate endpoint)
  createdAt: string;
  updatedAt: string;
  matchedValue?: number | null; // Amount allocated when fetched via transaction association
}

export interface Transaction {
  id: number;
  externalId: string | null; // Use for display (e.g., "TX-{externalId}")
  provider: TransactionProvider;
  branchId?: number;
  branch?: IDrop;
  customerId?: number
  customer?: ICustomer
  amount: number;
  type: TransactionType;
  session?: TransactionSessionType | null;
  status: TransactionStatus;
  currencyCode: string;
  transactionDate: string; // ISO date string
  processedDate?: string | null;
  nextAction?: TransactionNextAction | null;
  errorReason?: TransactionError | null;
  message?: string | null;
  createdAt: string;
  updatedAt: string;
  invoices?: Invoice[]; // For "Related Invoices" tab on Transaction Details
  matchedValue?: number | null; // Amount allocated when fetched via invoice association
}

export interface TransactionDetails extends Transaction {
  invoices: Invoice[]; // Included for "Related Invoices" tab
}

export interface OutstandingBalanceByDays {
  total: number;
  days7: number;
  days14: number;
  days30: number;
  days60: number;
  days90: number;
}

export interface ListInvoicesResponse {
  invoices: {
    result: Invoice[];
    count: number;
    pages: number;
  };
  outstandingBalance: number; // Total outstanding across ALL invoices (backward compatibility)
  outstandingBalanceByDays?: OutstandingBalanceByDays; // Outstanding balances by due date range
}

export interface ListTransactionsResponse {
  transactions: {
    result: Transaction[];
    count: number;
    pages: number;
  };
}

export interface ListInvoiceOrdersResponse {
  orders: {
    result: IOrder[];
    count: number;
    pages: number;
  };
}

export type ListInvoiceOrdersParams = {
  invoiceId: number;
  pagination: PaginatedParams<IOrder>;
  filters: FilterParams<IOrder> & {invoiceId: number};
};

type AccountingEndpointConfig = {
  LIST_INVOICES: EndpointConfig<
    ListInvoicesParams,
    ListInvoicesResponse
  >;
  LIST_CUSTOMER_INVOICES: EndpointConfig<
    ListCustomerInvoicesParams,
    ListInvoicesResponse
  >;
  LIST_TRANSACTIONS: EndpointConfig<
    ListTransactionsParams,
    ListTransactionsResponse
  >;
  LIST_CUSTOMER_TRANSACTIONS: EndpointConfig<
    ListCustomerTransactionsParams,
    ListTransactionsResponse
  >;
  GET_INVOICE_DETAILS: EndpointConfig<
    number,
    Invoice
  >;
  GET_TRANSACTION_DETAILS: EndpointConfig<
    number,
    TransactionDetails
  >;
  LIST_INVOICE_ORDERS: EndpointConfig<
    ListInvoiceOrdersParams,
    ListInvoiceOrdersResponse
  >;
};

export type ListInvoicesParams = {
  pagination: PaginatedParams<Invoice>;
  filters: FilterParams<Invoice & {transactionId: number, orderId: number, branchId?: number, customerId?: number}>;
};

export type ListTransactionsParams = {
  pagination: PaginatedParams<Transaction>;
  filters: FilterParams<Transaction & {invoiceId?: number, branchId?: number, customerId?: number}>;
};

export type ListCustomerInvoicesParams = {
  customerId: number;
  pagination: PaginatedParams<Invoice>;
  filters: FilterParams<Invoice>;
};

export type ListCustomerTransactionsParams = {
  customerId: number;
  pagination: PaginatedParams<Transaction>;
  filters: FilterParams<Transaction>;
};

const requests: RequestConfig<AccountingEndpointConfig> = {
  LIST_INVOICES: params => {
    // Send params as objects, not JSON strings (like working APIs)
    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/invoices`,
      method: "GET",
      params
    };
  },
  LIST_CUSTOMER_INVOICES: params => {
    const { customerId, ...rest } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/invoices/customer/${customerId}`,
      method: "GET",
      params: rest
    };
  },
  LIST_TRANSACTIONS: params => {
    // Send params as objects, not JSON strings (like working APIs)
    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/transactions`,
      method: "GET",
      params
    };
  },
  LIST_CUSTOMER_TRANSACTIONS: params => {
    // Branch-scoped endpoint for masquerade mode
    const { customerId, ...rest } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/transactions/customer/${customerId}`,
      method: "GET",
      params: rest
    };
  },
  GET_INVOICE_DETAILS: invoiceId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/invoices/${invoiceId}`,
      method: "GET"
    };
  },
  GET_TRANSACTION_DETAILS: transactionId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/transactions/${transactionId}`,
      method: "GET"
    };
  },
  LIST_INVOICE_ORDERS: params => {
    const { invoiceId, ...rest } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/accounting/invoices/${invoiceId}/orders`,
      method: "GET",
      params: rest
    };
  }
};

export const useAccountingApi: UseApiRequest<AccountingEndpointConfig> = (name, axiosOverrides) =>
  useAxios(requests[ name ], name, axiosOverrides);
